import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TrainingModuleCard } from '../components/training/TrainingModuleCard';
import { trainingService } from '../services/training.service';
import { algorithmsService, type Difficulty, type ProblemStatus } from '../services/algorithms.service';
import { FUNDAMENTALS, type FundamentalCategory } from '../data/fundamentals';

const FUNDAMENTAL_CATEGORIES: { key: FundamentalCategory; label: string; icon: string; description: string }[] = [
  { key: 'frontend', label: 'Frontend', icon: 'desktop_windows', description: 'Frameworks y librerías de UI para el navegador y mobile.' },
  { key: 'backend', label: 'Backend', icon: 'dns', description: 'Runtimes y frameworks para el lado del servidor.' },
  { key: 'languages', label: 'Lenguajes', icon: 'data_object', description: 'Lenguajes de programación y sus características.' },
  { key: 'cloud', label: 'Cloud', icon: 'cloud', description: 'Plataformas y servicios de infraestructura en la nube.' },
  { key: 'architecture', label: 'Arquitectura & Patrones', icon: 'architecture', description: 'Principios abstractos, patrones de diseño y arquitecturas de software.' },
  { key: 'ai', label: 'Inteligencia Artificial', icon: 'psychology', description: 'Fundamentos para construir aplicaciones con LLMs, RAG y agentes de IA.' },
];

const ALGO_TOPICS = [
  'Arrays', 'Hash Map', 'Stack', 'String', 'Sliding Window',
  'Binary Search', 'Dynamic Programming', 'Graphs', 'Trees', 'Linked List',
];

type Tab = 'modules' | 'algorithms' | 'interview-prep' | 'fundamentals';

const INTERVIEW_TYPES = [
  {
    slug: 'ai-system-design',
    title: 'AI System Design',
    description: 'Design LLM-powered systems, RAG pipelines, and multi-agent architectures.',
    icon: 'psychology',
    tags: ['LLMs', 'RAG', 'Agents'],
  },
  {
    slug: 'backend-system-design',
    title: 'Backend System Design',
    description: 'Design scalable distributed systems: APIs, databases, caching, and queues.',
    icon: 'dns',
    tags: ['Distributed', 'Databases', 'Scalability'],
  },
  {
    slug: 'frontend-system-design',
    title: 'Frontend System Design',
    description: 'Design performant frontend architectures: CDN, rendering strategies, and state management.',
    icon: 'web',
    tags: ['CDN', 'Performance', 'Rendering'],
  },
  {
    slug: 'data-engineering',
    title: 'Data Engineering',
    description: 'Design data pipelines, warehouses, streaming systems, and ETL architectures.',
    icon: 'storage',
    tags: ['Pipelines', 'Streaming', 'Warehouse'],
  },
  {
    slug: 'ml-system-design',
    title: 'ML System Design',
    description: 'Design ML training pipelines, model serving, feature stores, and monitoring.',
    icon: 'model_training',
    tags: ['Training', 'Serving', 'MLOps'],
  },
] as const;

function DifficultyBadge({ difficulty }: { difficulty: Difficulty }) {
  const styles: Record<Difficulty, string> = {
    EASY: 'bg-emerald-500/10 text-emerald-400',
    MEDIUM: 'bg-tertiary/10 text-tertiary',
    HARD: 'bg-error/10 text-error',
  };
  return (
    <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${styles[difficulty]}`}>
      {difficulty}
    </span>
  );
}

function StatusIcon({ status }: { status: ProblemStatus }) {
  if (status === 'SOLVED') {
    return <span className="material-symbols-outlined text-base text-emerald-400">check_circle</span>;
  }
  if (status === 'ATTEMPTED') {
    return <span className="material-symbols-outlined text-base text-tertiary">pending</span>;
  }
  return <span className="material-symbols-outlined text-base text-outline">radio_button_unchecked</span>;
}

export default function TrainingHomePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('modules');
  const [algoDifficulty, setAlgoDifficulty] = useState<Difficulty | ''>('');
  const [algoTopic, setAlgoTopic] = useState('');
  const [algoPage, setAlgoPage] = useState(1);

  const { data: algoStats } = useQuery({
    queryKey: ['algo-stats'],
    queryFn: () => algorithmsService.getStats(),
    enabled: tab === 'algorithms',
  });

  const { data: algoProblems, isLoading: algoLoading } = useQuery({
    queryKey: ['algo-problems', algoDifficulty, algoTopic, algoPage],
    queryFn: () =>
      algorithmsService.listProblems({
        difficulty: algoDifficulty || undefined,
        topic: algoTopic || undefined,
        page: algoPage,
      }),
    enabled: tab === 'algorithms',
  });

  const { data: trainings = [], isLoading } = useQuery({
    queryKey: ['trainings'],
    queryFn: () => trainingService.findAll(),
  });

  const { data: progress } = useQuery({
    queryKey: ['training-progress'],
    queryFn: () => trainingService.getProgress(),
    initialData: { total: 0, completed: 0, percentage: 0, records: [] },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100svh-3.5rem)] items-center justify-center md:min-h-screen">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex-1 max-w-7xl space-y-6 overflow-y-auto p-4 md:space-y-8 md:p-8 lg:p-12">
      <div className="flex items-end justify-between">
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Learning Path</p>
          <h1 className="text-2xl font-bold text-on-surface md:text-3xl">Training</h1>
        </div>
        {tab === 'modules' ? (
          <div className="text-right">
            <p className="text-2xl font-bold text-on-surface">{progress.percentage}%</p>
            <p className="text-xs text-on-surface-variant">
              {progress.completed}/{progress.total} lessons
            </p>
          </div>
        ) : null}
      </div>

      <div className="overflow-x-auto">
      <div data-tour="tour-training-tabs" className="flex min-w-max gap-1 rounded-2xl border border-outline-variant/10 bg-surface-container-low p-1 md:w-fit">
        {[
          { key: 'modules', icon: 'school', desktop: 'Video Modules', mobile: 'Modules' },
          { key: 'algorithms', icon: 'terminal', desktop: 'Algorithm Practice', mobile: 'Algorithms' },
          { key: 'interview-prep', icon: 'architecture', desktop: 'Interview Prep', mobile: 'Interview' },
          { key: 'fundamentals', icon: 'menu_book', desktop: 'Fundamentals', mobile: 'Fundamentos' },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => setTab(item.key as Tab)}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors md:flex-none md:px-4 ${
              tab === item.key
                ? 'bg-surface-container-high text-on-surface shadow-sm'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-base">{item.icon}</span>
            <span className="hidden sm:inline">{item.desktop}</span>
            <span className="sm:hidden">{item.mobile}</span>
          </button>
        ))}
      </div>
      </div>

      {tab === 'modules' ? (
        trainings.length === 0 ? (
          <div className="rounded-2xl bg-surface-container-low py-16 text-center">
            <span className="material-symbols-outlined mb-3 block text-5xl text-on-surface-variant">school</span>
            <p className="font-semibold text-on-surface">No modules available yet</p>
            <p className="mt-1 text-sm text-on-surface-variant">Content is being prepared. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {trainings.map((training) => (
              <TrainingModuleCard
                key={training.id}
                training={training}
                onOpen={() => navigate(`/training/modules/${training.id}`)}
              />
            ))}
          </div>
        )
      ) : null}

      {tab === 'algorithms' ? (
        <div className="space-y-6">
          {algoStats ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
              {[
                {
                  label: 'Solved',
                  value: `${algoStats.solved}/${algoStats.total}`,
                  sub: `${algoStats.total > 0 ? Math.round((algoStats.solved / algoStats.total) * 100) : 0}%`,
                  color: 'text-on-surface',
                },
                { label: 'Easy', value: algoStats.byDifficulty.EASY, color: 'text-emerald-400' },
                { label: 'Medium', value: algoStats.byDifficulty.MEDIUM, color: 'text-tertiary' },
                { label: 'Hard', value: algoStats.byDifficulty.HARD, color: 'text-error' },
              ].map((item) => (
                <div key={item.label} className="rounded-xl bg-surface-container-low p-4">
                  <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
                  {'sub' in item ? <p className="text-xs font-bold text-primary">{item.sub}</p> : null}
                  <p className="mt-0.5 text-xs text-on-surface-variant">{item.label}</p>
                </div>
              ))}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 rounded-lg bg-surface-container-low p-1">
              {(['', 'EASY', 'MEDIUM', 'HARD'] as const).map((d) => (
                <button
                  key={d}
                  onClick={() => { setAlgoDifficulty(d); setAlgoPage(1); }}
                  className={`rounded px-3 py-1.5 text-xs font-semibold transition-colors ${
                    algoDifficulty === d ? 'bg-surface-container-high text-on-surface' : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  {d || 'All'}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ALGO_TOPICS.map((t) => (
                <button
                  key={t}
                  onClick={() => { setAlgoTopic(algoTopic === t ? '' : t); setAlgoPage(1); }}
                  className={`rounded border px-2.5 py-1 text-xs font-medium transition-colors ${
                    algoTopic === t
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-outline-variant text-on-surface-variant hover:border-primary/50 hover:text-on-surface'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto overflow-hidden rounded-xl bg-surface-container-low">
            <div className="min-w-[560px]">
              <div className="grid grid-cols-[2rem_1fr_7rem_auto_6rem] gap-4 border-b border-outline-variant/10 px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                <span>#</span>
                <span>Title</span>
                <span>Difficulty</span>
                <span>Topics</span>
                <span className="text-right">Attempts</span>
              </div>
              {algoLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              ) : algoProblems?.problems.length === 0 ? (
                <div className="py-16 text-center text-sm text-on-surface-variant">No problems match the current filters.</div>
              ) : (
                <div className="divide-y divide-outline-variant/10">
                  {algoProblems?.problems.map((problem) => (
                    <button
                      key={problem.id}
                      onClick={() => navigate(`/training/algorithms/${problem.slug}`)}
                      className="group grid w-full grid-cols-[2rem_1fr_7rem_auto_6rem] items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-surface-container"
                    >
                      <StatusIcon status={problem.status} />
                      <div>
                        <span className="text-sm font-medium text-on-surface transition-colors group-hover:text-primary">
                          {problem.order}. {problem.title}
                        </span>
                      </div>
                      <DifficultyBadge difficulty={problem.difficulty} />
                      <div className="flex flex-wrap gap-1">
                        {problem.topics.slice(0, 2).map((t) => (
                          <span key={t} className="rounded bg-surface-container-high px-2 py-0.5 text-[10px] text-on-surface-variant">
                            {t}
                          </span>
                        ))}
                        {problem.topics.length > 2 ? (
                          <span className="text-[10px] text-on-surface-variant">+{problem.topics.length - 2}</span>
                        ) : null}
                      </div>
                      <span className="text-right text-xs text-on-surface-variant">
                        {problem.attempts > 0 ? problem.attempts : '—'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {algoProblems && algoProblems.pages > 1 ? (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setAlgoPage((p) => Math.max(1, p - 1))}
                disabled={algoPage === 1}
                className="rounded bg-surface-container px-3 py-1.5 text-sm text-on-surface-variant transition-colors hover:bg-surface-container-high disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-sm text-on-surface-variant">{algoPage} / {algoProblems.pages}</span>
              <button
                onClick={() => setAlgoPage((p) => Math.min(algoProblems.pages, p + 1))}
                disabled={algoPage === algoProblems.pages}
                className="rounded bg-surface-container px-3 py-1.5 text-sm text-on-surface-variant transition-colors hover:bg-surface-container-high disabled:opacity-40"
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === 'interview-prep' ? (
        <div className="space-y-4">
          <p className="text-sm text-on-surface-variant">
            Stage-gated AI interviews. Each type loads a specialized evaluation engine. Draw your architecture on the canvas and the AI sees it in real time.
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {INTERVIEW_TYPES.map((t) => (
              <button
                key={t.slug}
                onClick={() => navigate(`/training/system-design/${t.slug}`)}
                className="group flex items-start gap-4 rounded-xl bg-surface-container-low p-5 text-left transition-colors hover:bg-surface-container"
              >
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl primary-gradient">
                  <span className="material-symbols-outlined text-xl text-on-primary">{t.icon}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-bold text-on-surface transition-colors group-hover:text-primary">{t.title}</h2>
                  <p className="mt-1 text-xs leading-relaxed text-on-surface-variant">{t.description}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    {t.tags.map((tag) => (
                      <span key={tag} className="rounded bg-surface-container-high px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <span className="material-symbols-outlined mt-0.5 shrink-0 text-xl text-on-surface-variant">arrow_forward</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {tab === 'fundamentals' ? (
        <div className="space-y-8">
          <p className="text-sm text-on-surface-variant">
            Estudia los fundamentos de tecnologías clave, practica con quizzes y prepárate para entrevistas técnicas.
          </p>

          {FUNDAMENTAL_CATEGORIES.map((cat) => {
            const items = FUNDAMENTALS.filter((f) => f.category === cat.key);
            if (items.length === 0) return null;
            return (
              <div key={cat.key} className="space-y-3">
                {/* Cabecera de categoría */}
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-base text-primary">{cat.icon}</span>
                  <h2 className="text-sm font-bold uppercase tracking-widest text-on-surface">{cat.label}</h2>
                  <div className="h-px flex-1 bg-outline-variant/20" />
                </div>
                <p className="text-xs text-on-surface-variant">{cat.description}</p>

                {/* Grid de cards */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                  {items.map((tech) => (
                    <button
                      key={tech.slug}
                      onClick={() => navigate(`/training/fundamentals/${tech.slug}`)}
                      className="group flex flex-col items-start gap-3 rounded-xl bg-surface-container-low p-4 text-left transition-colors hover:bg-surface-container"
                    >
                      <div className="flex w-full items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-container-high">
                          <span className={`material-symbols-outlined text-xl ${tech.color}`}>{tech.icon}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-bold text-on-surface transition-colors group-hover:text-primary">
                            {tech.title}
                          </h3>
                        </div>
                        <span className="material-symbols-outlined shrink-0 text-base text-on-surface-variant opacity-0 transition-opacity group-hover:opacity-100">
                          arrow_forward
                        </span>
                      </div>
                      <p className="text-xs leading-relaxed text-on-surface-variant line-clamp-2">
                        {tech.shortDescription}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {['Conceptos', 'Quiz', 'Ejercicios'].map((tag) => (
                          <span
                            key={tag}
                            className="rounded bg-surface-container-high px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
